import { URLInitOptions } from '../../core/routing';
import { SW_QUERY_UPDATE_EVENT, SW_ROUTER_VIEW_QUERY_AVAILABLE_ATTRIBUTES } from './router-view.config';
import { defineElement } from '../../core/routing.util';

/**
 * `RouterViewQuery`: a NativeJS Web Component that contains query options for the `RouterView` component. It allows
 * to match multiple queries in one single RouterView.
 *
 * In order to get multiple queries to be matched, you must use the `RouterViewQuery` component to create
 * individual queries that will later get grouped to be matched together.
 *
 * You can describe the query you want to do using the following attributes:
 * - `slot="query"` - required, it will be appended to target slot inside the `<sw-router-view>` component
 * - `namespace` - required, every query option must be namespaced to some string
 * - `pathname` - the pathname you want to query
 * - `search-params` - the search parameters you want to query
 * - `hash` - the hash string you want to query
 *
 *
 * Usage:
 *
 *     <sw-router-view>
 *       <sw-router-view-query slot="query" namespace="car-preview" pathname="classA"></sw-router-view-query>
 *       <sw-router-view-query slot="query" namespace="financial-calculator" pathname="classA"></sw-router-view-query>
 *       Check more
 *     </sw-router-view>
 *
 * @export
 * @class RouterViewQuery
 * @extends {HTMLElement}
 */

export class RouterViewQuery extends HTMLElement {
  /**
   * The dynamic attributes supported by the `RouterViewQuery` web component.

   * @readonly
   * @static
   * @type {string[]}
   * @memberof RouterViewQuery
   */
  static get observedAttributes(): string[] {
    return SW_ROUTER_VIEW_QUERY_AVAILABLE_ATTRIBUTES;
  }

  /**
   * The `<sw-router-view-query>` component stylesheet. Mainly preventing
   * the element from rendering
   *
   * @readonly
   * @static
   * @type {string}
   * @memberof RouterViewQuery
   */
  private static stylesheet = `
    :host {
      display: none !important;
    }
  `;

  /**
   * The in-memory holder for all the query URL parts received via attributes
   * @ignore
   * @public
   * @readonly
   * @type {URLInitOptions}
   * @memberof RouterViewQuery
   */
  public readonly queryOptions: URLInitOptions;

  /**
   * The unique identifier (key) of the router view query element
   * @ignore
   * @public
   * @readonly
   * @type {symbol}
   * @memberof RouterViewQuery
   */
  public readonly queryKey: symbol;

  constructor() {
    super();
    this.queryOptions = {
      namespace: '',
      path: '',
      hash: '',
      searchParams: {},
    };
    this.queryKey = Symbol();

    // Prevent element from rendering
    const stylesheet = document.createElement('style');
    stylesheet.textContent = RouterViewQuery.stylesheet;
    const shadowRoot = this.attachShadow({ mode: 'closed' });
    shadowRoot.append(stylesheet);
  }

  /**
   * The Web Component API function that is called every time an attribute in the
   * `observedAttributes` array is modified from the outsides.
   *
   * It dispatches an event to the parent element that will update
   * the internal representation of all queries present on the router-view element
   *
   * Assumes that if invalid parameters are passed, match will handle them correctly
   *
   * @ignore
   * @param {string} name The name of the attribute that changed
   * @param {string} _oldValue The previous value of the attribute
   * @param {string} newValue The new value of the attribute
   * @memberof RouterViewQuery
   */
  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    switch (name) {
      case 'path':
      case 'pathname':
        this.queryOptions.path = newValue;
        break;
      case 'hash':
        this.queryOptions.hash = newValue;
        break;
      case 'search-params': {
        const paramEntries = newValue.split('&').map((param) => param.split('='));
        const paramObject = Object.fromEntries(paramEntries);
        this.queryOptions.searchParams = paramObject;
        break;
      }
      case 'namespace':
        this.queryOptions.namespace = newValue;
        break;
    }

    this.dispatchEvent(
      new CustomEvent(SW_QUERY_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          queryOptions: this.queryOptions,
          key: this.queryKey,
        },
      }),
    );
  }
}

/**
 *
 * @export
 * @param {string} [tagName='sw-router-view-query'] The router view query name
 */
export function define(tagName = 'sw-router-view-query'): void {
  defineElement(RouterViewQuery, tagName);
}
